import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injector } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';

import { formatNumber, formatTextDimension } from '../../../utils/number.format';
import { UNIT_OPTIONS } from '../../../models/list';
import { AqdbBatch } from '../../../../aqdb/models/aqdb-batch';
import {
	AqdbQualityDataService, QUALITY_DATA_BATCH_FIELD
} from '../../../../aqdb/services/aqdb-quality-data.service';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY, FilterDateFormatType,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { PaginatedResult } from '../../../models/paginated-result';
import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { TranslationService } from '../../../services/translation.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { CommonGridOptions } from '../../grid-options';
import { Strategy } from '../strategy';

export class StrategyAqdb implements Strategy<AqdbBatch> {
	aqdbQualityDataService: AqdbQualityDataService;
	selectionService: DatacenterSelectionService;
	translationService: TranslationService;
	defaultOrderBy = new OrderBy<keyof AqdbBatch>('batchReference', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];

	constructor(injector: Injector) {
		this.aqdbQualityDataService = injector.get<AqdbQualityDataService>(AqdbQualityDataService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
		this.translationService = injector.get<TranslationService>(TranslationService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof AqdbBatch>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then((qualityIssue: PaginatedResult<AqdbBatch>) => this.removeDeselectedRows(qualityIssue));
	}

	async getAllData(
		orderBy: OrderBy<keyof AqdbBatch>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		const fields = currentColumns.length > 0 ? <(keyof AqdbBatch)[]>currentColumns : QUALITY_DATA_BATCH_FIELD;
		return firstValueFrom(this.aqdbQualityDataService.getQualityData(
			fields,
			filter,
			0,
			60_000,
			orderBy,
			false
		));
	}

	formatCellByType(columnDef: ColDefWithFields<AqdbBatch>, data: AqdbBatch) {
		const fields = columnDef.fields || [];
		const field = columnDef.field || fields[0];
		const { columnType } = columnDef;
		const columnId = columnDef.colId;
		const columnGroupId = columnDef.groupId;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
				<string> data[<keyof AqdbBatch> fields[0]],
				<string> data[<keyof AqdbBatch> fields[1]],
				<string> data[<keyof AqdbBatch> fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof AqdbBatch> fields[0]]) {
				orderNumber = <string> data[<keyof AqdbBatch> fields[0]];
			}
			if (<string> data[<keyof AqdbBatch> fields[0]] && <string> data[<keyof AqdbBatch> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof AqdbBatch> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
			return this.fileformatNumberColumn(field, columnGroupId, columnId, data);
		case ColumnType.TEXT_COLUMN:
			return this.formatingTextColumn(field, columnGroupId, columnId, data);
		case ColumnType.DATE_COLUMN:
			if (data[<keyof AqdbBatch> field]) {
				const dateformat = (<FilterDateFormatType> columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment.utc(<string> data[<keyof AqdbBatch> field]).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	formatingTextColumn(field: string, groupId: string | undefined, colId: string | undefined, data?: AqdbBatch) {
		if (!colId || data === undefined) {
			return '';
		}
		if (colId === 'form') {
			return this.getTranslation(colId, data.form);
		} else if (colId === 'markedIdentity') {
			return data.segments.map(item => item.markedIdentity).join(';');
		}

		switch (groupId) {
		case 'mechanicalCharacteristicsTensile':
			return this.formatingTensileTextColumn(field, colId, data);
		case 'chemicalCharacteristics':
			return this.formatingChemicalTextColumn(field, colId, data);
		case 'metalCoatingCharacteristics':
			return this.formatingMetalCoatingTextColumn(field, colId, data);
		case 'roughnessCharacteristics':
			return this.formatingRoughnessTextColumn(field, colId, data);
		case 'organicCoatingCharacteristics':
			return this.formatingOrganicCoatingTextColumn(field, colId, data);
		case 'chemicalSurfaceTreatmentCharacteristics':
			return this.formatingChemicalSurfaceTreatmentTextColumn(field, colId, data);
		case 'magneticCharacteristics':
			return this.formatingMagneticTextColumn(field, colId, data);
		default:
			break;
		}
		return <string> data[<keyof AqdbBatch> field];
	}

	formatingTensileTextColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'sampleDirection':
			return data.segments.flatMap(item => item.tensileList)
				.map(tensile => this.getTranslation(colId, tensile.sampleDirectionCode)).join(';');
		case 'unitYs':
			return data.segments.flatMap(item => item.tensileList)
				.map(tensile => tensile.unitYs).join(';');
		case 'unitUTS':
			return data.segments.flatMap(item => item.tensileList)
				.map(tensile => tensile.unitUTS).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingChemicalTextColumn(field: string, colId: string, data: AqdbBatch) {
		if (colId === 'carbonEquivalentFormula') {
			return data.segments.flatMap(item => item.chemicalList)
				.map(chemical => chemical.carbonEquivalentFormula).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingMetalCoatingTextColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'spotNumber':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(metalCoating => metalCoating.spotNumber).join(';');
		case 'metalCoatingFace':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(metalCoating => metalCoating.face).join(';');
		case 'unit':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(metalCoating => metalCoating.unit).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingRoughnessTextColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'roughnessFace':
			return data.segments.flatMap(item => item.roughnessList)
				.map(roughness => roughness.face).join(';');
		case 'valueW':
			return data.segments.flatMap(item => item.roughnessList)
				.map(roughness => roughness.valueW).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingOrganicCoatingTextColumn(field: string, colId: string, data: AqdbBatch) {
		if (colId === 'organicCoatingFace') {
			return data.segments.flatMap(item => item.organicCoatingList)
				.map(organicCoating => organicCoating.face).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingChemicalSurfaceTreatmentTextColumn(field: string, colId: string, data: AqdbBatch) {
		if (colId === 'chemicalSurfaceTreatmentFace') {
			return data.segments.flatMap(item => item.chemicalSurfaceTreatmentList)
				.map(chemicalSurfaceTreatment => chemicalSurfaceTreatment.face).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	formatingMagneticTextColumn(field: string, colId: string, data: AqdbBatch) {
		if (colId === 'camaTypmth') {
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(magnetic => magnetic.camaTypmth).join(';');
		}
		return <string>data[<keyof AqdbBatch>field];
	}

	fileformatNumberColumn(field: string, groupId: string | undefined, colId: string | undefined, data?: AqdbBatch) {
		if (colId === undefined || data === undefined) {
			return '';
		}

		switch (colId) {
		case 'grossWeight':
			return `${ formatNumber(
				Number(data.qtyBatchGross),
				'weight'
			) } ${ UNIT_OPTIONS['to'].symbol }`;
		case 'netWeight':
			return `${ formatNumber(
				Number(data.qtyBatchNet),
				'weight'
			) } ${ UNIT_OPTIONS['to'].symbol }`;
		case 'anisotropyCoefficient':
			return data.segments.flatMap(item => item.anisotropyList)
				.map(item => String(item.anisotropyCoefficient)).join(';');
		}

		if (groupId) {
			switch (groupId) {
			case 'mechanicalCharacteristicsTensile':
				return this.formatingTensileNumberColumn(field, colId, data);
			case 'chemicalCharacteristics':
				return this.formatingChemicalNumberColumn(field, colId, data);
			case 'metalCoatingCharacteristics':
				return this.formatingMetalCoatingNumberColumn(field, colId, data);
			case 'roughnessCharacteristics':
				return this.formatingRoughnessNumberColumn(field, colId, data);
			case 'magneticCharacteristics':
				return this.formatingMagneticNumberColumn(field, colId, data);
			}
		}
		const value = <number | undefined >data[<keyof AqdbBatch> field];
		return value === undefined ? '' : String(value);
	}

	formatingTensileNumberColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'tensileTestNumber':
			return data.segments.flatMap(item => item.tensileList)
				.map(item => String(item.testNumber)).join(';');
		case 'valueYs':
			return data.segments.flatMap(item => item.tensileList)
				.map(item => item.valueYs === undefined
					? '' : String(item.valueYs)).join(';');
		case 'ultimateTensileStrength':
			return data.segments.flatMap(item => item.tensileList)
				.map(item => item.ultimateTensileStrength === undefined
					? '' : String(item.ultimateTensileStrength)).join(';');
		case 'elongation':
			return data.segments.flatMap(item => item.tensileList)
				.map(item => item.elongation === undefined
					? '' : String(item.elongation)).join(';');
		case 'strainHardeningRatio':
			return data.segments.flatMap(item => item.tensileList)
				.map(item => item.strainHardeningRatio === undefined
					? '' : String(item.strainHardeningRatio)).join(';');
		}
		const value = <number | undefined>data[<keyof AqdbBatch>field];
		return value === undefined ? '' : String(value);
	}

	formatingChemicalNumberColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'contentAl':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentAl === undefined
					? '' : String(item.contentAl)).join(';');
		case 'contentC':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentC === undefined
					? '' : String(item.contentC)).join(';');
		case 'contentCr':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentCr === undefined
					? '' : String(item.contentCr)).join(';');
		case 'contentMn':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentMn === undefined
					? '' : String(item.contentMn)).join(';');
		case 'contentNb':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentNb === undefined
					? '' : String(item.contentNb)).join(';');
		case 'contentP':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentP === undefined
					? '' : String(item.contentP)).join(';');
		case 'contentS':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentS === undefined
					? '' : String(item.contentS)).join(';');
		case 'contentSi':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentSi === undefined
					? '' : String(item.contentSi)).join(';');
		case 'contentTi':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentTi === undefined
					? '' : String(item.contentTi)).join(';');
		case 'contentV':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.contentV === undefined
					? '' : String(item.contentV)).join(';');
		case 'carbonEquivalentValue':
			return data.segments.flatMap(item => item.chemicalList)
				.map(item => item.carbonEquivalentValue === undefined
					? '' : String(item.carbonEquivalentValue)).join(';');
		}
		const value = <number | undefined>data[<keyof AqdbBatch>field];
		return value === undefined ? '' : String(value);
	}

	formatingMetalCoatingNumberColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'border1Quantity':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(item => item.border1Quantity === undefined
					? '' : String(item.border1Quantity)).join(';');
		case 'border2Quantity':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(item => item.border2Quantity === undefined
					? '' : String(item.border2Quantity)).join(';');
		case 'centerQuantity':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(item => item.centerQuantity === undefined
					? '' : String(item.centerQuantity)).join(';');
		case 'averageQuantity':
			return data.segments.flatMap(item => item.metalCoatingList)
				.map(item => item.averageQuantity === undefined
					? '' : String(item.averageQuantity)).join(';');
		}
		const value = <number | undefined>data[<keyof AqdbBatch>field];
		return value === undefined ? '' : String(value);
	}

	formatingRoughnessNumberColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'valueRa':
			return data.segments.flatMap(item => item.roughnessList)
				.map(item => item.valueRa === undefined
					? '' : String(item.valueRa)).join(';');
		case 'cutOff':
			return data.segments.flatMap(item => item.roughnessList)
				.map(item => item.cutOff === undefined
					? '' : String(item.cutOff)).join(';');
		}
		const value = <number | undefined>data[<keyof AqdbBatch>field];
		return value === undefined ? '' : String(value);
	}

	formatingMagneticNumberColumn(field: string, colId: string, data: AqdbBatch) {
		switch (colId) {
		case 'losses1T':
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(item => item.losses1T === undefined
					? '' : String(item.losses1T)).join(';');
		case 'losses1T5':
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(item => item.losses1T5 === undefined
					? '' : String(item.losses1T5)).join(';');
		case 'induction2500w':
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(item => item.induction2500w === undefined
					? '' : String(item.induction2500w)).join(';');
		case 'induction5000w':
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(item => item.induction5000w === undefined
					? '' : String(item.induction5000w)).join(';');
		case 'induction10000W':
			return data.segments.flatMap(item => item.magneticCharacteristicList)
				.map(item => item.induction10000W === undefined
					? '' : String(item.induction10000W)).join(';');
		}
		const value = <number | undefined>data[<keyof AqdbBatch>field];
		return value === undefined ? '' : String(value);
	}

	getTranslation(colId: string | undefined, value: string) {
		if (colId === 'form' && value) {
			return this.translationService.getTR(`form.indif.${ value }`);
		} else if (colId === 'sampleDirection') {
			if (value === 'T') {
				return this.translationService.getTR('aqdb.direction.T');
			} else if (value === 'L') {
				return this.translationService.getTR('aqdb.direction.L');
			}
		}
		return '';
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions): CommonGridOptions<AqdbBatch> {
		return new CommonGridOptions({
			...defaultGridOptions,
			getRowId: ({ data }: { data: AqdbBatch }) => data.id
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<string>[],
		currentColumns: string[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<AqdbBatch>,
		rowsCountSubject: BehaviorSubject<number | undefined>
	) {
		const currentColumnsWithId = Array.from(new Set(this.addIdColumn(currentColumns)));
		return this.aqdbQualityDataService.getQualityData(
			<(keyof AqdbBatch)[]>currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			<OrderBy<keyof AqdbBatch>[]> orderBy,
			false
		).subscribe({
			next: res => {
				if (res.count < 1) {
					params.api.showNoRowsOverlay();
				} else {
					params.api.hideOverlay();
				}
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				rowsCountSubject.next(res.count);
			},
			error: (err: unknown) => {
				console.error('error requesting quality data', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: string[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	private removeDeselectedRows(aqdbBatch: PaginatedResult<AqdbBatch>) {
		return aqdbBatch.results.filter(aqdb => {
			const rowDeselected = this.selectionService.findDeselectedNode(aqdb.id);
			return !rowDeselected;
		});
	}
}
